import { KalturaRuleType } from '@kaltura-ott/ott-client-types';
import { KalturaUserAssetRule } from '@kaltura-ott/ott-client-types/api/types/KalturaUserAssetRule';
import { FormatKalturaProductPricePpv, getUserRules, KuxError, KuxSeriesType, Provider } from '@kaltura-ott/tvpil';
import { computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { errorHandling } from '../../utils/error-handling';
import { ErrorPriority } from '../error';
import { ProvidersRepository } from '../providersRepository';

import { MediaAsset } from './MediaAsset';

export class Series extends MediaAsset<KuxSeriesType> {
  @observable rules?: KalturaUserAssetRule[] = [];
  @observable price?: FormatKalturaProductPricePpv;
  @observable provider?: string;
  private providersRepository: ProvidersRepository;

  constructor(data: KuxSeriesType) {
    super(data);
    this.providersRepository = container.resolve<ProvidersRepository>('providersRepository');
    this.provider = this.providersRepository.calculateAssetProviders(this.data.metas);
  }

  async setupRules(): Promise<void> {
    this.rules = await lastValueFrom(getUserRules(this.data.id, 1));
  }

  @computed get isGeoBlocked(): boolean {
    return !!this.getRuleByType(KalturaRuleType.geo);
  }

  @computed get geoBlockingProperties(): {
    description?: string;
    name?: string;
  } {
    const rule = this.getRuleByType(KalturaRuleType.geo);
    if (rule) {
      return {
        description: rule.description,
        name: rule.name,
      };
    }
    return {};
  }

  @computed get isAdult(): boolean {
    return this.data.adult;
  }

  @computed get isRented(): boolean {
    return false;
  }

  get mediaFileId(): number | undefined {
    return this.data.mediaFileId;
  }

  public getRuleByType(type: KalturaRuleType): KalturaUserAssetRule | undefined {
    return computed(() => {
      return this.rules?.find((rule) => rule.ruleType === type);
    }).get();
  }

  @computed get isProviderAsset(): boolean {
    return !!this.provider;
  }

  @computed get providerLogo(): string | undefined {
    return this.providersRepository.providerAssetLogo(this.provider);
  }

  @computed get providerSettings(): Provider | undefined {
    if (this.provider) {
      return this.providersRepository?.providerSettings[this.provider];
    }
  }

  @computed get isDeviceAllowedDeepLink(): boolean | undefined {
    return this.providersRepository?.isDeviceAllowedDeepLink(this.provider);
  }

  @computed get isDeviceAllowedGoDirectlyToStore(): boolean | undefined {
    return this.providersRepository?.isDeviceAllowedGoDirectlyToStore(this.provider);
  }

  @computed get isPurchased(): boolean | undefined {
    if (this.isProviderAsset) {
      return true;
    }
  }

  @errorHandling({ type: ErrorPriority.medium })
  watch(): string | undefined {
    if (!this.isProviderAsset) {
      return undefined;
    }

    if (this.data.metas?.Deeplink) {
      return this.data.metas?.Deeplink?.toString();
    } else {
      throw new KuxError('Content is not supported');
    }
  }
}
